import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { Nullable } from '@ps/utils';
import {
  AdvancedFeasibilityFilters,
  PatchableMetaData,
  LocaleChangeTargeting,
  Survey,
  SurveyLocaleData,
  SurveyLocationUploadResult,
  SurveyMarginFormState,
  SurveyMetadataFormState,
  SurveyQualificationsFormState,
  SurveyQuotasFormState,
} from '@ps/survey-utils';

import {
  SurveyChildKey,
  WithRecalculation,
} from '../survey-creation-shared-state.model';
import { WithScope } from '../survey-creation-child-registry.helper';
import { SuppliersMetaData } from './survey-creation-suppliers-meta-data-state.model';

/**
 * The Audience step's own actions — pre-draft, create and edit. The save
 * family, discard, and reset are genuinely multi-stage and live on
 * `SurveyCreationCoreActions` instead.
 */
export const SurveyCreationAudienceActions = createActionGroup({
  source: 'Survey Creation Audience',
  events: {
    // recalculationsEnabled defaults true at the facade.
    Init: props<WithRecalculation<{ surveyId: Nullable<number> }>>(),
    'Create Standard Survey': emptyProps(),
    'Change Created Survey Locale': props<{
      locale: SurveyLocaleData;
      deleteCurrent: boolean;
    }>(),
    'Create Survey After Locale Change': props<{
      locale: SurveyLocaleData;
      targeting: LocaleChangeTargeting;
      deleteSurveyId: Nullable<number>;
    }>(),
    'Create Survey From Template': props<{
      templateId: number;
      deleteSurveyId: Nullable<number>;
    }>(),
    'Update Metadata':
      props<WithScope<{ metadata: SurveyMetadataFormState }>>(),
    'Update Qualifications':
      props<WithScope<{ qualifications: SurveyQualificationsFormState }>>(),
    'Update Quotas': props<WithScope<{ quotas: SurveyQuotasFormState }>>(),
    'Update Margin': props<{ margin: SurveyMarginFormState }>(),
    'Settle Buyer Cpi': emptyProps(),
    /** The operator confirmed an above-threshold Buyer CPI. */
    'Confirm High Cpi': props<{ cpi: number }>(),
    /** No surveyId yet, so the activity-log entry has to wait for one. */
    'Queue High Cpi Confirmation': props<{ cpi: number }>(),
    'Clear High Cpi Confirmation Queue': emptyProps(),
    'Toggle Manual Processing': props<{
      companyId: number;
      enabled: boolean;
    }>(),
    /**
     * Commits the Feasibility drawer's pending Advanced Filters.
     *
     * `clearUploadedPsidsFor` is legacy parity: when the committed
     * type switched from Include to Exclude, the surveyId is passed
     * to fire the PSID clear call for that survey.
     */
    'Apply Advanced Filters': props<{
      filters: AdvancedFeasibilityFilters;
      clearUploadedPsidsFor: Nullable<number>;
    }>(),
    'Upload Location Codes': props<{
      countryCode: string;
      qualificationCode: number;
      qualificationName: string;
      file: File;
    }>(),
    'Clear Location Codes': props<{
      qualificationCode: number;
      qualificationName: string;
    }>(),
    'Download Location Codes Template': props<{
      countryCode: string;
      qualificationCode: number;
    }>(),
    'Download Validated Location File': props<{
      qualificationCode: number;
    }>(),
    'Select Tags': props<{ tagIds: number[] }>(),
    'Set Suppliers Meta Data Blend Id': props<{ blendId: string }>(),
    'Set Suppliers Meta Data': props<{
      metaData: Partial<SuppliersMetaData>;
    }>(),
    Reset: emptyProps(),
  },
});

/** Pre-draft is create-stage only: launch and edit never build children. */
export const SurveyCreationPreDraftActions = createActionGroup({
  source: 'Survey Creation Pre Draft',
  events: {
    'Build Pre Draft Children': props<{ localeData: SurveyLocaleData[] }>(),
    'Select All Pre Draft Children': emptyProps(),
    'Clear All Pre Draft Children': emptyProps(),
    'Select Pre Draft Children': props<{ ids: SurveyChildKey[] }>(),
  },
});

export const SurveyCreationAudienceApiActions = createActionGroup({
  source: 'Survey Creation Audience API',
  events: {
    'Create Standard Survey Success': props<{
      survey: Survey;
      replacedSurveyId: Nullable<number>;
    }>(),
    'Create Standard Survey Failure': props<{ error: string }>(),
    'Create Survey From Template Success': props<{
      survey: Survey;
      replacedSurveyId: Nullable<number>;
    }>(),
    'Create Survey From Template Failure': props<{ error: string }>(),
    'Upload Location Codes Success': props<{
      qualificationCode: number;
      qualificationName: string;
      result: SurveyLocationUploadResult;
    }>(),
    'Upload Location Codes Failure': props<{
      qualificationCode: number;
      error: string;
    }>(),
    // Tags are an Audience-step concern: the tag drawer only exists on
    // create/edit, and `tagLinks` lives in this slice's child state.
    'Load Survey Tags Success': props<{ tagIds: number[] }>(),
    'Load Survey Tags Failure': props<{ error: string }>(),
    'Link Survey Tags Success': props<{ tagIds: number[] }>(),
    'Link Survey Tags Failure': props<{ error: string }>(),
  },
});

export const SurveyCreationChildrenActions = createActionGroup({
  source: 'Survey Creation Children',
  events: {
    'Select Child': props<{ surveyId: number }>(),
    'Switch Child': props<{ surveyId: number }>(),
    'Update Child Survey Cpi': props<{ surveyId: number; cpi: number }>(),
    'Patch Child Metadata': props<WithScope<{ changes: PatchableMetaData }>>(),
  },
});

export const SurveyCreationChildrenApiActions = createActionGroup({
  source: 'Survey Creation Children API',
  events: {
    'Load Child Surveys Success': props<{ surveys: Survey[] }>(),
    'Load Child Surveys Failure': props<{ error: string }>(),
    'Update Child Survey Cpi Success': props<{ survey: Survey }>(),
    'Update Child Survey Cpi Failure': props<{
      surveyId: number;
      error: string;
    }>(),
  },
});

export const SurveyCreationMcActions = createActionGroup({
  source: 'Survey Creation MC',
  events: {
    'Create Mc Traditional Surveys': emptyProps(),
    'Create Mc Grouped Project': emptyProps(),
    'Add Survey Child': props<SurveyLocaleData>(),
    'Delete Survey Child': props<{ surveyId: number }>(),
  },
});

export const SurveyCreationMcApiActions = createActionGroup({
  source: 'Survey Creation MC API',
  events: {
    'Create Mc Traditional Surveys Success': props<{ survey: Survey }>(),
    'Create Mc Traditional Surveys Failure': props<{
      failedLocales: string[];
      createdSurveys: Survey[];
    }>(),
    'Create Mc Grouped Project Success': props<{ survey: Survey }>(),
    'Create Mc Grouped Project Failure': props<{ error: string }>(),
    'Add Survey Child Success': props<{ survey: Survey }>(),
    'Add Survey Child Failure': props<{ error: string }>(),
    'Delete Survey Child Success': props<{ surveyId: number }>(),
    'Delete Survey Child Failure': props<{ error: string }>(),
  },
});
